import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { FiChevronDown, FiSave, FiEye, FiEyeOff, FiAlertTriangle } from "react-icons/fi";
import {
  useAddSmsCredentialMutation,
  useUpdateSmsCredentialMutation,
} from "../../../features/admin/smsCredential/smsCredentialApi";
import { Drawer, Modal, Button, Input, Label, Spinner } from "../../../components/ui";
import { PROVIDERS, getProvider, credentialFields, labelFor, isSecretField } from "./smsProviders";

const token = () => sessionStorage.getItem("access_token");

// Add / edit a single SMS credential. On add you can assign it to a company
// (tenant_id) or leave it Admin / Global; new credentials start Inactive.
// On edit only api_credential and entity_id change (provider type is fixed).
const SmsCredentialEditor = ({
  mode = "add",
  record = null,
  companies = [],
  existingCreds = [],
  initialTenantId = null,
  onClose,
  onSaved,
}) => {
  const isEdit = mode === "edit";
  const [addSmsCredential, { isLoading: adding }] = useAddSmsCredentialMutation();
  const [updateSmsCredential, { isLoading: updating }] = useUpdateSmsCredentialMutation();
  const saving = adding || updating;

  const [providerName, setProviderName] = useState(isEdit ? record?.type : PROVIDERS[0].name);
  const provider = useMemo(() => getProvider(providerName), [providerName]);

  // owner: null = Admin / Global, or a tenant_id
  const [tenantId, setTenantId] = useState(
    isEdit ? record?.tenant_id ?? null : initialTenantId ?? null,
  );
  const [confirm, setConfirm] = useState(null); // { owner, creds } when already present

  const [values, setValues] = useState(() => {
    const seed = {};
    const p = getProvider(isEdit ? record?.type : PROVIDERS[0].name);
    (p?.fields || []).forEach((f) => {
      seed[f] = f === "entity_id" ? record?.entity_id || "" : record?.api_credential?.[f] || "";
    });
    return seed;
  });
  const [revealed, setRevealed] = useState({});

  const ownerName =
    tenantId == null ? "Admin / Global" : companies.find((c) => c.tenant_id === tenantId)?.name || `Client ${tenantId}`;

  const onProviderChange = (name) => {
    setProviderName(name);
    const p = getProvider(name);
    const next = {};
    (p?.fields || []).forEach((f) => (next[f] = ""));
    setValues(next);
  };

  const setField = (f, v) => setValues((prev) => ({ ...prev, [f]: v }));

  const doAdd = async () => {
    const apiCredential = {};
    credentialFields(provider).forEach((f) => (apiCredential[f] = values[f].trim()));
    try {
      await addSmsCredential({
        token: token(),
        body: {
          api_credential: apiCredential,
          entity_id: values.entity_id.trim(),
          type: providerName,
          tenant_id: tenantId, // null → Admin / Global
        },
      }).unwrap();
      toast.success("Credential added");
      onSaved?.();
    } catch (err) {
      toast.error(err?.data?.message || "Something went wrong");
    }
  };

  const handleSave = async () => {
    const missing = (provider?.fields || []).some((f) => !String(values[f] || "").trim());
    if (missing) {
      toast.error("Please fill all fields");
      return;
    }

    if (isEdit) {
      const apiCredential = {};
      credentialFields(provider).forEach((f) => (apiCredential[f] = values[f].trim()));
      try {
        await updateSmsCredential({
          token: token(),
          id: Number(record.id),
          body: { api_credential: apiCredential, entity_id: values.entity_id.trim() },
        }).unwrap();
        toast.success("Credential updated");
        onSaved?.();
      } catch (err) {
        toast.error(err?.data?.message || "Something went wrong");
      }
      return;
    }

    // add mode — warn if this owner already has credential(s)
    const ownerCreds = existingCreds.filter((c) => (c.tenant_id ?? null) === (tenantId ?? null));
    if (ownerCreds.length > 0) {
      setConfirm({ creds: ownerCreds });
      return;
    }
    await doAdd();
  };

  const handleDrawerClose = () => {
    if (confirm) return; // let the confirmation dialog close first
    onClose();
  };

  return (
    <Drawer
      width="md"
      onClose={handleDrawerClose}
      title={isEdit ? "Edit SMS Credential" : "Add SMS Credential"}
      chips={[
        { label: isEdit ? record?.type : providerName, tone: "blue" },
        { label: ownerName, tone: "gray" },
      ]}
      footer={
        <>
          <span className="text-xs text-gray-500">
            {isEdit ? "Updates credential fields" : "New credentials start Inactive"}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving}
              icon={saving ? <Spinner /> : <FiSave className="h-4 w-4" />}
            >
              {saving ? "Saving…" : isEdit ? "Save" : "Add credential"}
            </Button>
          </div>
        </>
      }
    >
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        {/* assign to company (add only) */}
        {!isEdit && (
          <div>
            <Label>Assign to</Label>
            <div className="relative">
              <select
                value={tenantId ?? ""}
                onChange={(e) => setTenantId(e.target.value === "" ? null : Number(e.target.value))}
                className="block w-full pl-3 pr-9 py-2.5 border border-gray-300 rounded-lg bg-white text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all"
              >
                <option value="">Admin / Global</option>
                {companies.map((c) => (
                  <option key={c.tenant_id} value={c.tenant_id}>
                    {c.name} (ID: {c.tenant_id})
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>
        )}

        {/* provider */}
        <div>
          <Label>Provider</Label>
          {isEdit ? (
            <div className="px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-600 capitalize">
              {providerName}
            </div>
          ) : (
            <div className="relative">
              <select
                value={providerName}
                onChange={(e) => onProviderChange(e.target.value)}
                className="block w-full pl-3 pr-9 py-2.5 border border-gray-300 rounded-lg bg-white text-sm capitalize appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all"
              >
                {PROVIDERS.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          )}
        </div>

        {/* dynamic fields */}
        {(provider?.fields || []).map((f) => {
          const secret = isSecretField(f);
          const show = revealed[f];
          return (
            <div key={f}>
              <Label>{labelFor(provider, f)} *</Label>
              <div className="relative">
                <Input
                  type={secret && !show ? "password" : "text"}
                  value={values[f] || ""}
                  onChange={(e) => setField(f, e.target.value)}
                  className={secret ? "pr-10" : ""}
                  autoComplete="off"
                />
                {secret && (
                  <button
                    type="button"
                    onClick={() => setRevealed((p) => ({ ...p, [f]: !p[f] }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {show ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* already-present confirmation */}
      {confirm && (
        <Modal
          size="sm"
          onClose={() => setConfirm(null)}
          title={
            <span className="inline-flex items-center gap-2">
              <FiAlertTriangle className="h-4 w-4 text-amber-500" /> Credential already present
            </span>
          }
          subtitle={`${ownerName} already has ${confirm.creds.length} credential${
            confirm.creds.length === 1 ? "" : "s"
          }`}
          footer={
            <>
              <Button variant="outline" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={async () => {
                  setConfirm(null);
                  await doAdd();
                }}
              >
                Add anyway
              </Button>
            </>
          }
        >
          <p className="text-sm text-gray-600 mb-3">
            This owner is already set up with the following. A new credential will be added as{" "}
            <span className="font-medium">Inactive</span> — activate it later to switch.
          </p>
          <ul className="space-y-2">
            {confirm.creds.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2"
              >
                <span className="text-sm font-medium text-gray-800 capitalize">{c.type}</span>
                <span className="text-xs text-gray-500">entity {c.entity_id || "—"}</span>
                <span
                  className={`text-xs font-semibold ${
                    String(c.status) === "1" ? "text-emerald-600" : "text-gray-400"
                  }`}
                >
                  {String(c.status) === "1" ? "Active" : "Inactive"}
                </span>
              </li>
            ))}
          </ul>
        </Modal>
      )}
    </Drawer>
  );
};

export default SmsCredentialEditor;
